import { useMemo } from 'react';
import isEqual from 'lodash/isEqual';

import type { EventFieldConfig } from '../../../../config/tpoGenConfig';
import { normalizeValue, normalizeRecord } from '../../../../utils/formNormalize';

interface UseEventDirtyStateArgs {
    fieldConfigs: readonly EventFieldConfig[];
    formValues: Record<string, unknown>;
    fetchedFormValues: Record<string, unknown> | null;
    isEditMode: boolean;
}

interface UseEventDirtyStateReturn {
    isValid: boolean;
    isDirty: boolean;
    canSave: boolean;
}

const useEventDirtyState = ({
    fieldConfigs,
    formValues,
    fetchedFormValues,
    isEditMode,
}: UseEventDirtyStateArgs): UseEventDirtyStateReturn => {
    const requiredFieldNames = useMemo(
        () => fieldConfigs.filter((f) => f.required && f.name).map((f) => f.name!),
        [fieldConfigs],
    );

    const isValid = useMemo(
        () =>
            requiredFieldNames.every((name) => {
                const v = formValues[name];
                return normalizeValue(v) !== null;
            }),
        [requiredFieldNames, formValues],
    );

    const normalizedBaseline = useMemo(
        () => (fetchedFormValues ? normalizeRecord(fetchedFormValues) : null),
        [fetchedFormValues],
    );

    const isDirty = useMemo(() => {
        if (isEditMode) {
            if (!normalizedBaseline) return false;
            const allKeys = new Set([...Object.keys(normalizedBaseline), ...Object.keys(formValues)]);
            for (const key of allKeys) {
                const baseline = normalizedBaseline[key] ?? null;
                const current = normalizeValue(formValues[key]);
                if (!isEqual(baseline, current)) return true;
            }
            return false;
        }
        return Object.values(formValues).some((v) => normalizeValue(v) !== null);
    }, [isEditMode, formValues, normalizedBaseline]);

    const canSave = useMemo(
        () => (isEditMode ? isValid && isDirty : isValid),
        [isEditMode, isValid, isDirty],
    );

    return { isValid, isDirty, canSave };
};

export default useEventDirtyState;
